import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { CheckCircle2, Calendar, Video, Mail, ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import { API } from '../utils/api';

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString('es-CL', {
      weekday: 'long', day: '2-digit', month: 'long',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function AsesoriaConfirmacionPage() {
  const [params] = useSearchParams();
  const bookingId = params.get('booking_id');
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!bookingId) { setLoading(false); return; }
    let cancelled = false;
    const fetchBooking = async (retriesLeft = 4) => {
      try {
        const r = await axios.get(`${API}/asesoria-booking/${bookingId}`);
        if (cancelled) return;
        setBooking(r.data);
        // If meet_link not yet available, poll a few times (background task may still be running)
        if (!r.data.meet_link && retriesLeft > 0) {
          setTimeout(() => fetchBooking(retriesLeft - 1), 2500);
        } else {
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    };
    fetchBooking();
    return () => { cancelled = true; };
  }, [bookingId]);

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      <title>Asesoría confirmada | SinAdicciones.org</title>
      <meta name="robots" content="noindex, follow" />
      <Header />
      <main className="flex-1 py-16">
        <div className="container mx-auto px-4 max-w-2xl">
          {loading ? (
            <div className="text-center text-slate-400 py-20">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-emerald-400" />
              <p>Confirmando tu reserva...</p>
            </div>
          ) : !booking ? (
            <div className="bg-slate-900/60 border border-amber-500/30 rounded-2xl p-8 text-center">
              <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-3">No encontramos tu reserva</h1>
              <p className="text-slate-400 text-sm mb-6">
                Si pagaste y crees que es un error, escríbenos a <a href="mailto:hector@sinadicciones.org" className="text-emerald-400 underline">hector@sinadicciones.org</a> y lo resolvemos al instante.
              </p>
              <Link to="/asesoria">
                <Button className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold">Volver a Asesoría</Button>
              </Link>
            </div>
          ) : (
            <div className="bg-slate-900/60 border border-emerald-500/30 rounded-2xl p-8 sm:p-10">
              <div className="flex flex-col items-center text-center mb-8">
                <div className="w-16 h-16 rounded-full bg-emerald-500/20 border-2 border-emerald-500/50 flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-9 h-9 text-emerald-400" />
                </div>
                <h1 className="text-3xl font-bold text-white mb-2">¡Reserva confirmada!</h1>
                <p className="text-slate-400">Tu sesión con <span className="text-white font-semibold">{booking.asesor_name}</span> está agendada.</p>
              </div>

              <div className="bg-slate-800/40 rounded-xl p-6 mb-6 space-y-4">
                <div className="flex gap-3">
                  <Calendar className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Fecha y hora</p>
                    <p className="text-white font-semibold capitalize">{fmtDate(booking.slot_utc)}</p>
                    <p className="text-xs text-slate-500">Duración: {booking.duration_min} min · hora Chile</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Video className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Videollamada</p>
                    {booking.meet_link ? (
                      <a href={booking.meet_link} target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:underline break-all text-sm" data-testid="meet-link">
                        {booking.meet_link}
                      </a>
                    ) : (
                      <p className="text-slate-300 text-sm">Te llegará el link al email <strong className="text-white">{booking.email}</strong> en los próximos minutos.</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-3">
                  <Mail className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Confirmación por email</p>
                    <p className="text-white text-sm">Enviada a {booking.email}</p>
                  </div>
                </div>
              </div>

              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5 mb-6">
                <p className="text-emerald-200 text-sm leading-relaxed">
                  <strong className="text-emerald-300">Antes de tu sesión:</strong> ten papel y lápiz a mano. Héctor ya tiene tu caso preparado y te entregará una recomendación personalizada del camino terapéutico adecuado.
                </p>
              </div>

              <div className="text-xs text-slate-500 space-y-1 mb-6">
                <p>• Si necesitas reagendar, escribe a <a className="text-emerald-400" href="mailto:hector@sinadicciones.org">hector@sinadicciones.org</a> con al menos 12h de anticipación.</p>
                <p>• Booking ID: <code className="text-slate-400">{booking.booking_id}</code></p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/" className="flex-1">
                  <Button variant="outline" className="w-full border-slate-700 text-slate-300 hover:bg-slate-800">Volver al inicio</Button>
                </Link>
                <Link to="/centros-de-rehabilitacion-drogas-chile" className="flex-1">
                  <Button className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold">
                    Explorar centros <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
